import { trackInternalEvent } from '@rio/tracking'
import find from 'lodash/find'
import { filter } from 'lodash'
import { useState, ComponentType, useRef, useCallback, useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { i18n as I18n } from 'builder/utils/i18n'
import DialogModal from 'builder/components/DialogModal'
import {
  Job,
  JobCreatePayload,
  JobMovePayload,
  actions as jobTrackingActions,
  selectors as jobTrackingSelectors,
} from 'builder/modules/jobTracking'
import { selectors as jobSearchSelectors } from 'builder/modules/jobSearch'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { useUser } from 'builder/hooks/useUser'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { FetchStatuses } from 'builder/modules/constants'
import { useConfig } from 'builder/hooks/useConfig'
import tunerURL from 'images/modals/resume.jpg'
import { CioLimitModal } from '../CioLimitModal/CioLimitModal'
import BoardPlaceholder from './BoardPlaceholder'
import { Sidebar } from './Sidebar'
import { Container } from './styles'
import ChromeExtensionNotification from './ChromeExtensionNotification'
import CardDrawer from './CardDrawer'
import { CardStatusDrawer } from './CardStatusDrawer/CardStatusDrawer'
import { BoardProps } from './Board'

const JobTracking = () => {
  const user = useUser()

  const {
    isCioLimitModal,
    openCardDrawer,
    openStatusChangeDrawer,
    jobs,
    fetchStatus,
    columnNames,
  } = useTypedSelector(jobTrackingSelectors.selectJobTracking)
  const isJobsLoading = fetchStatus !== FetchStatuses.loaded
  const autoApplyBubbles = useTypedSelector(jobSearchSelectors.autoApplyBubbles)

  const dispatch = useDispatch()

  const [editId, setEditId] = useState<number | null>(null)
  const [interviewJob, setInterviewJob] = useState<Partial<Job> | null>(null)
  const [deleteId, setDeleteId] = useState<number | null>(null)
  const [BoardComponent, setBoardComponent] = useState<ComponentType<BoardProps> | undefined>(
    undefined,
  )
  const [lastUpdatedId, setLastUpdatedId] = useState<number | null>(null)
  const statusDrawerRef = useRef<HTMLDivElement>(null)
  const config = useConfig()

  const isSuperApp = config?.features.superApp
  const { isPhone } = useMediaQueries()

  const clearOpenDrawers = useCallback(() => {
    if (isPhone) {
      dispatch(jobTrackingActions.setOpenCardDrawer(false))
      dispatch(jobTrackingActions.setOpenStatusChangeDrawer(false))
    }
  }, [isPhone])

  // const [canInstallExtension, setCanInstallExtension] = useState(false)

  // useEffect(() => {
  //   const isDesktopChrome = /Chrome/i.test(navigator.userAgent) && !isPhone
  //   const isExtensionInstalled = document.getElementById('jtrack-root') !== null
  //   setCanInstallExtension(isDesktopChrome && !isExtensionInstalled)
  // }, [isPhone])

  useEffect(() => {
    if (!autoApplyBubbles.running) {
      dispatch(
        jobTrackingActions.fetchJobsRequest({
          location: 'Remote',
          dummy: autoApplyBubbles.running,
        }),
      )
    }

    return () => {
      dispatch(jobTrackingActions.setResumeSelection(null))
    }
  }, [autoApplyBubbles.running])

  useEffect(() => {
    import(/* webpackChunkName: "job-tracking-board" */ './Board').then(module => {
      setBoardComponent(() => module.default)
    })
  }, [])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const cardIdParam = params.get('card_id')

    if (cardIdParam && !isJobsLoading) {
      const cardIdNum = Number(cardIdParam)
      setEditId(cardIdNum)
      dispatch(jobTrackingActions.setOpenCardDrawer(true))

      window.history.pushState({}, '', `${window.location.origin}${window.location.pathname}`)
    }
  }, [isJobsLoading])

  const createJob = useCallback(
    (fields: JobCreatePayload) => {
      dispatch(jobTrackingActions.createJobRequest({ ...fields, dummy: autoApplyBubbles.running }))
      trackInternalEvent('create_job_tracking_card')
    },
    [autoApplyBubbles.running],
  )

  const deleteJob = useCallback(() => {
    if (deleteId !== null) {
      dispatch(jobTrackingActions.deleteJobRequest({ id: deleteId }))
    }
    setDeleteId(null)
    setEditId(null)
    clearOpenDrawers()
  }, [deleteId, clearOpenDrawers])

  const handleCardClick = useCallback((id: number, openDrawer = true) => {
    setEditId(id)
    if (openDrawer) {
      dispatch(jobTrackingActions.setOpenCardDrawer(true))
    }
  }, [])

  const handleDeleteClick = useCallback((id: number) => {
    setDeleteId(id)
  }, [])

  const handleEditModalClose = useCallback(() => {
    setEditId(null)
    clearOpenDrawers() // Close mobile drawers if open
  }, [clearOpenDrawers])

  const handleDeleteModalClose = useCallback(() => {
    setDeleteId(null)
  }, [])

  const handleEditJob = useCallback(
    (patch: Job) => {
      dispatch(jobTrackingActions.editJobRequest(patch))

      setLastUpdatedId(patch.id)
      setInterviewJob(patch)
    },
    [jobs, isSuperApp],
  )

  const handleMove = useCallback(
    ({ source, destination }: JobMovePayload) => {
      dispatch(jobTrackingActions.moveRequest({ source, destination }))

      const movedJob = filter(jobs, { status: source.droppableId })[source.index]
      if (movedJob) {
        setLastUpdatedId(movedJob.id)
      }
    },
    [jobs],
  )

  const handleSetInterviewJob = useCallback((selectedJob: Partial<Job> | null) => {
    setInterviewJob(selectedJob)
  }, [])

  const handleCloseLimitModal = useCallback(() => {
    dispatch(jobTrackingActions.setCioLimitModal(false))
    trackInternalEvent('skip_paywall')
  }, [])

  const isFreePlan = !user?.hasPremiumFeatures
  const editableJob = useMemo(
    () => (editId !== null ? find(jobs, { id: editId }) : null),
    [editId, jobs],
  )
  const lastUpdatedJob = useMemo(
    () => (lastUpdatedId !== null ? find(jobs, { id: lastUpdatedId }) : null),
    [lastUpdatedId, jobs],
  )
  const isBoardLoaded = BoardComponent && !isJobsLoading

  const shouldRenderCardDrawer = editId !== null && isPhone && openCardDrawer
  const shouldRenderStatusDrawer =
    editId !== null && editableJob && isPhone && openStatusChangeDrawer

  return (
    <>
      <Container>
        {/* {canInstallExtension && <JobTrackerExtensionBanner componentType="job_tracker_top" />} */}

        {isBoardLoaded && BoardComponent ? (
          <BoardComponent
            columnNames={columnNames}
            jobs={jobs}
            move={handleMove}
            reorder={payload => dispatch(jobTrackingActions.reorderRequest(payload))}
            lastUpdatedJob={lastUpdatedJob}
            onCardCreate={createJob}
            onCardClick={handleCardClick}
            isFreePlan={isFreePlan}
            deleteJob={deleteJob}
            handleSetInterviewJob={handleSetInterviewJob}
            interviewJob={interviewJob}
            fromDashboard={false}
          />
        ) : (
          <BoardPlaceholder />
        )}

        <DialogModal
          dontShowAgainId={`${user?.id}_deleteItem_jobTracking`}
          title={I18n.t('builder.job_tracking.delete_job')}
          text={I18n.t('builder.job_tracking.delete_modal.sure_to_delete_job')}
          primaryButtonText={I18n.t('builder.job_tracking.delete')}
          secondaryButtonText={I18n.t('builder.job_tracking.cancel')}
          isOpen={!!deleteId} // Control visibility based on deleteId state
          primaryAction={deleteJob} // Use the local delete handler
          secondaryAction={handleDeleteModalClose}
          closeAction={handleDeleteModalClose}
        />
      </Container>

      {editId !== null && !isPhone && (
        <Sidebar
          job={editableJob}
          columnNames={columnNames}
          onSubmit={handleEditJob}
          onClose={handleEditModalClose}
          onDelete={handleDeleteClick}
          handleSetInterviewJob={handleSetInterviewJob}
          isDashboard={false}
        />
      )}

      {shouldRenderCardDrawer && (
        <CardDrawer fromDashboard={false}>
          <Sidebar
            stateChangeRef={statusDrawerRef}
            job={editableJob}
            columnNames={columnNames}
            onSubmit={handleEditJob}
            onClose={handleEditModalClose}
            onDelete={handleDeleteClick}
            handleSetInterviewJob={handleSetInterviewJob}
            isDashboard={false}
          />
        </CardDrawer>
      )}

      {shouldRenderStatusDrawer && (
        <CardStatusDrawer
          ref={statusDrawerRef}
          selectedJob={editableJob}
          columnNames={columnNames}
          editJob={handleEditJob}
          onClose={() => dispatch(jobTrackingActions.setOpenStatusChangeDrawer(false))}
          onDelete={handleDeleteClick}
        />
      )}

      <ChromeExtensionNotification />

      {isCioLimitModal && (
        <CioLimitModal
          documentType="resume"
          cancelButton={I18n.t('builder.job_tracking.limit_modal.cancel')}
          upgradebutton={I18n.t('builder.job_tracking.limit_modal.upgrade')}
          tunerImg={tunerURL}
          onClose={handleCloseLimitModal}
        />
      )}
    </>
  )
}

export default JobTracking
